import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, Circle, Plus, Trash, PencilSimple, Broom, WarningCircle, ClockCounterClockwise, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRIORITY = {
  high: { label: "High", cls: "border-rose-200 bg-rose-50 text-rose-800" },
  medium: { label: "Medium", cls: "border-amber-200 bg-amber-50 text-amber-800" },
  low: { label: "Low", cls: "border-emerald-200 bg-emerald-50 text-emerald-800" },
};

export default function TodosPage() {
  const { user } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDone, setShowDone] = useState(true);
  const [busy, setBusy] = useState(false);

  // New-todo form state
  const [form, setForm] = useState({ title: "", priority: "medium", due_date: "", notes: "" });

  // Edit-in-place state (single row at a time)
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/todos", { params: { include_done: true } });
      setTodos(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load To-Do list");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  const open = useMemo(() => todos.filter((t) => !t.done), [todos]);
  const done = useMemo(() => todos.filter((t) => t.done), [todos]);
  const overdue = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return open.filter((t) => t.due_date && t.due_date < today).length;
  }, [open]);

  async function addTodo(e) {
    e?.preventDefault?.();
    const title = form.title.trim();
    if (!title) return;
    setBusy(true);
    try {
      const payload = {
        title,
        priority: form.priority,
        due_date: form.due_date || null,
        notes: form.notes.trim() || null,
      };
      const { data } = await api.post("/todos", payload);
      setTodos((cur) => [data, ...cur]);
      setForm({ title: "", priority: "medium", due_date: "", notes: "" });
      toast.success("Added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  }

  async function toggleDone(t) {
    try {
      const { data } = await api.put(`/todos/${t.id}`, { done: !t.done });
      setTodos((cur) => cur.map((x) => (x.id === t.id ? data : x)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  }

  function beginEdit(t) {
    setEditingId(t.id);
    setEditDraft({
      title: t.title || "",
      priority: t.priority || "medium",
      due_date: t.due_date || "",
      notes: t.notes || "",
    });
  }

  async function saveEdit(t) {
    if (!editDraft.title?.trim()) { toast.error("Title cannot be empty"); return; }
    try {
      const { data } = await api.put(`/todos/${t.id}`, {
        title: editDraft.title,
        priority: editDraft.priority,
        due_date: editDraft.due_date || null,
        notes: editDraft.notes || null,
      });
      setTodos((cur) => cur.map((x) => (x.id === t.id ? data : x)));
      setEditingId(null);
      setEditDraft({});
      toast.success("Saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  }

  async function removeTodo(t) {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await api.delete(`/todos/${t.id}`);
      setTodos((cur) => cur.filter((x) => x.id !== t.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  }

  async function clearCompleted() {
    if (!done.length) return;
    if (!window.confirm(`Clear ${done.length} completed item${done.length > 1 ? "s" : ""}?`)) return;
    try {
      const { data } = await api.post("/todos/clear-completed");
      setTodos((cur) => cur.filter((x) => !x.done));
      toast.success(`Cleared ${data.cleared} item${data.cleared === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear");
    }
  }

  const visibleDone = showDone ? done : [];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="todos-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2 flex items-center gap-2"><LockKey size={12} /> Private to you</div>
          <h1 className="section-title text-3xl sm:text-4xl">My To-Do List</h1>
          <p className="text-sm text-muted-foreground mt-1">
            A private checklist for <b>{user?.name || "you"}</b>. Only you can see these — no other user, not even admins.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Open</span>
            <span className="font-heading font-black text-base" data-testid="todos-count-open">{open.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Done</span>
            <span className="font-heading font-black text-base" data-testid="todos-count-done">{done.length}</span>
          </div>
          {overdue > 0 && (
            <div className="flex items-center gap-1.5 text-rose-700 dark:text-rose-400" data-testid="todos-count-overdue">
              <WarningCircle size={14} weight="fill" />
              <span>{overdue} overdue</span>
            </div>
          )}
        </div>
      </div>

      {/* New To-Do card */}
      <form onSubmit={addTodo} className="border border-border rounded-md bg-card p-4 grid grid-cols-1 md:grid-cols-[minmax(260px,1fr)_140px_160px_auto] gap-3" data-testid="todos-add-form">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">New item</Label>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Follow up with BDO on statement"
            className="border-border"
            maxLength={300}
            data-testid="todo-input-title"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Priority</Label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="border-border" data-testid="todo-input-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Due (optional)</Label>
          <Input
            type="date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className="border-border"
            data-testid="todo-input-due"
          />
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={busy || !form.title.trim()} data-testid="todo-add-btn">
            <Plus size={14} className="mr-2" /> Add
          </Button>
        </div>
      </form>

      {/* Open list */}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="section-caption">Open</div>
            <div className="font-heading font-bold tracking-tight">{open.length} item{open.length === 1 ? "" : "s"}</div>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={showDone} onCheckedChange={(v) => setShowDone(!!v)} data-testid="todos-toggle-show-done" />
              <span>Show completed</span>
            </label>
            {done.length > 0 && (
              <Button variant="ghost" size="sm" className="border border-border" onClick={clearCompleted} data-testid="todos-clear-completed">
                <Broom size={14} className="mr-2" /> Clear completed
              </Button>
            )}
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : open.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground italic">Nothing on your list — enjoy the calm.</div>
        ) : (
          <ul className="divide-y divide-border">
            {open.map((t) => (
              <TodoRow
                key={t.id} todo={t}
                editing={editingId === t.id}
                editDraft={editDraft}
                setEditDraft={setEditDraft}
                onToggle={toggleDone}
                onEdit={beginEdit}
                onCancel={() => { setEditingId(null); setEditDraft({}); }}
                onSave={saveEdit}
                onDelete={removeTodo}
                today={today}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Done list */}
      {showDone && (
        <div className="border border-border rounded-md bg-card overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <ClockCounterClockwise size={14} className="text-muted-foreground" />
            <div>
              <div className="section-caption">Completed</div>
              <div className="font-heading font-bold tracking-tight">{done.length} item{done.length === 1 ? "" : "s"}</div>
            </div>
          </div>
          {visibleDone.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground italic">Nothing completed yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {visibleDone.map((t) => (
                <TodoRow
                  key={t.id} todo={t}
                  editing={false}
                  editDraft={{}}
                  setEditDraft={() => {}}
                  onToggle={toggleDone}
                  onEdit={() => {}}
                  onCancel={() => {}}
                  onSave={() => {}}
                  onDelete={removeTodo}
                  today={today}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function TodoRow({ todo: t, editing, editDraft, setEditDraft, onToggle, onEdit, onCancel, onSave, onDelete, today }) {
  const overdue = !t.done && t.due_date && t.due_date < today;
  const p = PRIORITY[t.priority] || PRIORITY.medium;

  if (editing) {
    return (
      <li className="p-4 grid grid-cols-1 md:grid-cols-[1fr_140px_160px_auto] gap-3 items-end bg-muted/30" data-testid={`todo-edit-${t.id}`}>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Title</Label>
          <Input value={editDraft.title || ""} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} className="border-border" data-testid={`todo-edit-title-${t.id}`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Priority</Label>
          <Select value={editDraft.priority || "medium"} onValueChange={(v) => setEditDraft({ ...editDraft, priority: v })}>
            <SelectTrigger className="border-border"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Due</Label>
          <Input type="date" value={editDraft.due_date || ""} onChange={(e) => setEditDraft({ ...editDraft, due_date: e.target.value })} className="border-border" />
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onSave(t)} data-testid={`todo-save-${t.id}`}>Save</Button>
          <Button size="sm" variant="outline" className="border-border" onClick={onCancel} data-testid={`todo-cancel-${t.id}`}>Cancel</Button>
        </div>
      </li>
    );
  }

  return (
    <li className="p-4 flex items-start gap-3" data-testid={`todo-row-${t.id}`}>
      <button
        type="button"
        onClick={() => onToggle(t)}
        className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={t.done ? "Mark as open" : "Mark as done"}
        data-testid={`todo-toggle-${t.id}`}
      >
        {t.done
          ? <CheckCircle size={20} weight="fill" className="text-emerald-600" />
          : <Circle size={20} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className={`font-medium ${t.done ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${p.cls}`}>{p.label}</span>
          {t.due_date && (
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${overdue ? "border-rose-200 bg-rose-50 text-rose-800" : "border-border bg-muted/40 text-muted-foreground"}`}>
              Due {t.due_date}{overdue ? " · overdue" : ""}
            </span>
          )}
        </div>
        {t.notes && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{t.notes}</div>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!t.done && (
          <Button size="sm" variant="ghost" onClick={() => onEdit(t)} className="border border-border" data-testid={`todo-edit-btn-${t.id}`}>
            <PencilSimple size={14} />
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => onDelete(t)} className="border border-border text-rose-700 hover:text-rose-800" data-testid={`todo-delete-${t.id}`}>
          <Trash size={14} />
        </Button>
      </div>
    </li>
  );
}
